import i18next from 'i18next'
import React from 'react'
import { Form } from 'react-bootstrap'
import { navigate } from 'widgets-for-react'
import { formatDateDDMonthYYYY } from '../../utils/date'
import { Image } from '../../utils/image'
import { searchResultsSrc, searchResultsSrcSet } from '../../utils/imageSizes'
import { Link } from '../../utils/link'
import { DepartmentIcon } from '../../utils/spaarneIcons'
import { getClassName } from '../../utils/utils'
import { useInView } from '../hooks/useInView'
import { formClasses } from '../sharedLayout/forms'
import { GenericPageHeader } from '../sharedLayout/pageheader'
import { SearchPageState } from './searchPageState'

type SearchPageLayoutProps = {
  items: SearchPageState
  oldSearchTerm: string
  setSearchTerm: (s: string) => void
  breadcrumbs: { title: string; url: string }[]
}

export const SearchPageLayout = (props: SearchPageLayoutProps) => (
  <SearchPageLayoutAux
    items={props.items}
    oldSearchTerm={props.oldSearchTerm}
    setSearchTerm={props.setSearchTerm}
    breadcrumbs={props.breadcrumbs}
  />
)

const SearchPageLayoutAux = (props: SearchPageLayoutProps) => {
  const standardSuggestions = [
    i18next.t('search:spellingSuggestion1'),
    i18next.t('search:spellingSuggestion2'),
    i18next.t('search:spellingSuggestion3'),
  ]

  const count: number | undefined =
    props.items.paginationState.kind == 'loaded' ? props.items.paginationState.value : undefined

  const suggestions: string[] =
    props.items.searchResultState.kind == 'loaded'
      ? props.items.searchResultState.value.suggestions.length != 0
        ? props.items.searchResultState.value.suggestions
        : standardSuggestions
      : standardSuggestions

  const hasSuggestions =
    props.items.searchResultState.kind == 'loaded' && props.items.searchResultState.value.suggestions.length != 0

  const isEmptyState = !props.oldSearchTerm && count != undefined

  const isNoResults = props.oldSearchTerm != '' && count == 0

  const { inView, ref } = useInView(
    { threshold: 0.1 },
    (inview) => console.debug("SearchPageLayoutAux inView:", inview),
    false
  )

  return (
    <>
      <GenericPageHeader
        kind="search"
        title={
          props.oldSearchTerm
            ? i18next.t('search:searchResultsFor', { term: props.oldSearchTerm })
            : i18next.t('search:search')
        }
        breadcrumbs={props.breadcrumbs}
      />

      <section
        key={`block-search-results`}
        id={`io-search-results`}
        ref={ref}
        className={"block--search-results" + ' ' + (inView ? 'inview' : '')}
      >
        <div className="block__inner container--m gutter--m">
          <form
            aria-live="polite"
            onSubmit={(e) => {
              e.preventDefault()
              document.documentElement.lang == "nl" ? navigate(`/zoeken?query=${props.items.searchTerm}`) : navigate(`/en/search?query=${props.items.searchTerm}`)
            }}
          >
            {/* ----- Search bar & Filters */}

            <div className="block__bar searchbar searchbar--xl section--bg-secondary">
              <Form.Group controlId="search-results" className={getClassName(formClasses, 'formGroupClassName', '')}>
                <div className="searchbar__input">
                  <Form.Control
                    type="search"
                    inputMode="search"
                    placeholder={i18next.t('search:placeholder')}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => props.setSearchTerm(e.target.value)}
                    className={getClassName(formClasses, 'formInputClassName', '')}
                  />
                  <button className="searchbar__button" type="submit" title="Zoeken">
                    <span className="sr-text">{i18next.t('search:search')}</span>
                    <svg aria-hidden="true">
                      <use xlinkHref="#icon--SG-zoeken" />
                    </svg>
                  </button>
                </div>
              </Form.Group>

              {/* ----- Filters */}

              {!isEmptyState && (
                <div className="block__filters typo--xs">
                  <p className="block__filter">{i18next.t('search:searchResultsFound', { count: count })}</p>
                </div>
              )}
            </div>

            {/* ----- Results */}

            <div className="block__results">
              {isEmptyState ? (
                <div className="text--styled">
                  <h2>{i18next.t('search:suggestions')}</h2>
                  <ul>
                    <li>
                      <Link url={i18next.t('search:suggestion1Url')}>{i18next.t('search:suggestion1Text')}</Link>
                    </li>
                    <li>
                      <Link url={i18next.t('search:suggestion2Url')}>{i18next.t('search:suggestion2Text')}</Link>
                    </li>
                    <li>
                      <Link url={i18next.t('search:suggestion3Url')}>{i18next.t('search:suggestion3Text')}</Link>
                    </li>
                  </ul>
                </div>
              ) : isNoResults ? (
                <div className="text--styled">
                  <h2>{i18next.t('search:noResultsTitle')}</h2>
                  <p>{i18next.t('search:noResultsText')}</p>

                  <div>
                    <h3>{i18next.t('search:suggestions')}</h3>
                    {hasSuggestions && <p>{i18next.t('search:didYouMean')}</p>}
                    <ul>
                      {suggestions.map((suggestion: string, index: number) => (
                        <li key={index}>
                          <Link url={document.documentElement.lang == 'nl' ? `/zoeken?query=${suggestion}` : `/en/search?query=${suggestion}`}>{suggestion}</Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ) : (
                props.items.searchResultState.kind == 'loaded' && (
                  <ul className="block__list list--clickable list--teasers">
                    {props.items.searchResultState.value.hits.map((item, i) => {
                      const isNews = item.label == 'Nieuws'
                      const isEvent = item.label == 'Evenement'
                      const isFolder = item.label == 'Folder' || item.label == 'Folder pagina'
                      const image =
                        item.label == 'Afdeling' || item.label == 'Subafdeling' ? (
                          <div className="list__visual list__visual--svg">
                            {item.departmentTitle && (
                              <DepartmentIcon
                                className="list__icon"
                                width="50px"
                                height="50px"
                                department={item.departmentTitle}
                              />
                            )}
                          </div>
                        ) : isFolder ? (
                          <div className="list__visual list__visual--svg">
                            <svg aria-hidden="true" className="list__icon" width="50px" height="50px">
                              <use xlinkHref="#icon--SG-folders" />
                            </svg>
                          </div>
                        ) : !item.image ? (
                          <div className="list__visual list__visual--svg" />
                        ) : (
                          <div className="list__visual list__visual--img">
                            <Image
                              src={searchResultsSrc}
                              srcset={searchResultsSrcSet}
                              urls={item.image.urls}
                              lazyload={true}
                              alt={item.image.alt}
                              classNames={{ image: 'list__image' }}
                            />
                          </div>
                        )
                      // const label =
                      //   isNews || isEvent
                      //     ? `<span>${item.label} | <time dateTime=${
                      //         isNews ? item.publishingDate?.toISOString() : item.eventDate?.toISOString()
                      //       }>
                      //     ${
                      //       isNews
                      //         ? formatDateDDMonthYYYY(i18next, item.publishingDate || '')
                      //         : formatDateDDMonthYYYY(i18next, item.eventDate || '')
                      //     }
                      //   </time></span>`
                      //     : item.label
                      const contentType = item.label != 'Pagina' && item.label != 'error' ? item.label : undefined
                      const renderDateTime = isNews ? item.publishingDate?.toISOString() : item.eventDate?.toISOString()
                      const renderDateDDMonthYYYY = (date?: Date) =>
                        isNews
                          ? formatDateDDMonthYYYY(item.publishingDate || '')
                          : formatDateDDMonthYYYY(item.eventDate || '')
                      const date =
                        isNews || isEvent
                          ? `<time dateTime=${renderDateTime}>${renderDateDDMonthYYYY()}</time>`
                          : ''
                      return (
                        <li className="list__item" key={i}>
                          <Link url={item.url} className="list__link">
                            <article className="list__article">
                              {image}

                              <div className="list__content">
                                {contentType && <p className="list__label">{contentType}</p>}
                                <h2
                                  className="list__title"
                                  dangerouslySetInnerHTML={{
                                    __html: Highlighter(item.title, props.oldSearchTerm),
                                  }}
                                />
                                {(item.description || item.eventDate || item.publishingDate) && (
                                  <p
                                    className="list__description"
                                    dangerouslySetInnerHTML={{
                                      __html:
                                        (item.eventDate || item.publishingDate)
                                          ? date +
                                          (item.description
                                            ? ` | ${Highlighter(item.description, props.oldSearchTerm)}`
                                            : '')
                                          : item.description
                                            ? Highlighter(item.description, props.oldSearchTerm)
                                            : '',
                                    }}
                                  />
                                )}
                              </div>
                            </article>
                          </Link>
                        </li>
                      )
                    })}
                  </ul>
                )
              )}
            </div>
          </form>
        </div>
      </section>
    </>
  )
}

const Highlighter = (text: string, searchTerm: string): string => {
  const i1: number = text.toLowerCase().indexOf(searchTerm.toLowerCase())
  const i2: number = text.toLowerCase().indexOf(searchTerm.toLowerCase()) + searchTerm.length - 1

  const t =
    i1 == -1
      ? text
      : i1 == 0
        ? i2 == text.length - 1
          ? `<span class="text--primary">${text}</span>`
          : `<span class="text--primary">${text.substring(i1, i2 + 1)}</span><span>${text.substring(i2 + 1)}</span>`
        : i2 == text.length - 1
          ? `<span>${text.substring(0, i1)}</span><span class="text--primary">${text.substring(i1)}</span>`
          : `<span>${text.substring(0, i1)}</span><span class="text--primary">${text.substring(
            i1,
            i2 + 1
          )}</span><span>${text.substring(i2 + 1)}</span>`

  return t
}
